import * as React from 'react';
import { FieldSchemaInfo, Metadata } from '../../../types/Metadata';
import './itemCard.scss';
import { cn, getItemStatusId, getItemType, parseField } from '../../../utils';
import { getSchema } from '../../../utils';
import { NodeType } from '../../../enums/NodeType';
import { View } from '../../../types';
import { ProgressBar } from '../../ProgressBar/progressBar';
import { StatusCategories } from '../../../types/StatusCategory';
import { UserSettings } from '../../../types/UserSetting';
import URLManager from '../../../utils/urlManager';
import { ItemCardActions } from './itemCardActions';
import { ItemExpiryField } from './Field/itemExpiryField';
import { ItemField } from './Field/itemField';
import { ItemFieldProps } from './Field/types';
import { LinkField } from './Field/linkField';
import { DaysInWorkflowField } from './Field/daysInWorkflowField';
import { InThePlanField } from './Field/inThePlanField';
import { useSelector } from '../../../utils/useSelector';
import { BooleanField } from './Field/booleanField';
import { CriticalField } from './Field/criticalField';
import { TaskPendingKIsField } from './Field/taskPendingKIsField';
import * as ItemTypeIcon from '../../Icon/itemTypeIcon';
import TooltipPopover from '../../Popover/popover';
import { ContextMenuLink } from '../../ContextMenuLink/contextMenuLink';

export interface ItemCardProps {
  item: Metadata;
  group: View.Group;
  view: View.Setting;
  children?: React.ReactNode;
  displayName?: string;
}
const TaskPendingKIFieldName = 'taskPendingKIs';

const FIELDS = {
  link: LinkField,
  boolean: BooleanField,
  [TaskPendingKIFieldName]: TaskPendingKIsField,
  ['Item.expiryDate']: ItemExpiryField,
  ['Item.inThePlan']: InThePlanField,
  ['Item.critical']: CriticalField,
  ['Item.daysInCurrentStatus']: DaysInWorkflowField,
  ['Item.daysInCurrentDesignStatus']: DaysInWorkflowField
};

const getFieldComponent = (f: Pick<ItemFieldProps, 'field' | 'fieldInfo'>) => {
  const dataType = f.fieldInfo?.schemaField?.dataType;
  return FIELDS[f.field] || FIELDS[dataType] || ItemField;
};

export const ItemCard = ({
  item,
  group,
  view,
  children,
  displayName
}: ItemCardProps) => {
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const isSelected = useSelector(s => s.form?.data?.id == item.id);
  const statusClassName = useSelector(s =>
    getProgressBarClassName(
      item,
      s.planning?.statusCategories,
      s.view?.selected,
      s.planning?.userSettings
    )
  );
  const fields = useSelector(s =>
    getFields(s.view?.selected, s.planning?.userSettings)
  );
  const { updateUrl, wid, vid, c, m } = URLManager.useRouter();
  const itemSchema = getSchema(schemas, item.labels);
  if (!itemSchema) console.log('Missing schema for item type', item.labels);
  const df = itemSchema?.props?.displayField;
  const name = displayName ? displayName : item.props[df];
  const cardFields = fields.reduce((arr, f) => {
      const fieldInfo = parseField(f.field, schemas);
      const { schemaField, schema, schemaType } = fieldInfo;
      if (!schemaField || !schema) {
        //console.warn('Field schema not found', f);
        return arr;
      }
      if (schemaType == itemSchema?.props.type && fieldInfo.field == df) {
        return arr;
      }
      arr.push({ ...f, fieldInfo });
      return arr;
  }, [] as (View.CardField & { fieldInfo: FieldSchemaInfo })[]);

  const showStatusBar = !!statusClassName;
  const itemType = getItemType(item);
  const FormIcon = ItemTypeIcon[itemType];
  let description;
  const itemTypes = useSelector(s => s.planning?.itemTypes);
  if (itemTypes) {
    const value = itemTypes[itemType];
    if (value) description = value;
  }

  const hasPreAppliedItemType =
    !!view?.props?.preApplyFilters?.length &&
    view?.props?.preApplyFilters?.some(filter => {
      return (
        filter?.field == 'Item.itemType' &&
        filter?.conditions?.some(
          condition => !!condition?.is && condition.is.length == 1
        )
      );
    });
  return (
    <div className="item-card-container">
      <ItemCardActions item={item} view={view} group={group} />
      <ContextMenuLink
        className={cn('item-card slider-activator', {
          selected: isSelected,
          'fs-mask': view?.isMyItemsView
        })}
        href={URLManager.generateUrl(wid, {
          v: vid,
          c,
          m,
          id: item.props.refId
        })}
        onNavigate={() => {
          updateUrl({ id: item.props.refId });
        }}
      >
        <div data-id={item.id}>
          <div className="item-card-header">
            {!hasPreAppliedItemType && (
              <div className="item-card-icon">
                <TooltipPopover
                  packed={true}
                  type="info"
                  pointerPosition="top"
                  content={
                    <div className="popover-description-container">
                      <div>{description}</div>
                    </div>
                  }
                >
                  <div>{FormIcon ? <FormIcon /> : <></>}</div>
                </TooltipPopover>
              </div>
            )}
            <div className="item-card-name">
              {name ? name : <i>Untitled</i>}
            </div>
          </div>
          {children}
          {cardFields.map(f => {
            const FC = getFieldComponent(f);
            return (
              <FC
                key={f.field}
                item={item}
                itemSchema={itemSchema}
                view={view}
                group={group}
                {...f}
              />
            );
          })}
          {showStatusBar && <ProgressBar className={statusClassName} />}
        </div>
      </ContextMenuLink>
    </div>
  );
};

const getFields = (view: View.Setting, userSettings: UserSettings) => {
  if (!view?.props) return [];
  const viewSetting = userSettings[view.props.refId];
  const hideCardDetails = viewSetting?.props?.hideCardDetails;
  if (view.props.data == NodeType.Item && hideCardDetails) {
    return [];
  } else {
    return view?.card?.props?.fields || [];
  }
};

const getProgressBarClassName = (
  item: Partial<Metadata>,
  statusCategories: StatusCategories,
  view: View.Setting,
  userSettings: UserSettings = {}
) => {
  if (!item.labels?.includes(NodeType.Item)) return null;
  let itemStatusId = getItemStatusId(item);
  if (!view?.props) return null;
  const viewSettings = userSettings[view.props.refId];
  if (viewSettings?.props?.hideItemProgress) return null;
  const cat = statusCategories ? statusCategories[itemStatusId] : null;
  const catId = cat?.props?.id || 0;
  if (catId == 1) {
    if (itemStatusId == 50 || itemStatusId == 1) return 'workflow-bar-idea';
    if ([10, 12].includes(itemStatusId)) return 'workflow-bar-conceptreview';
    if (itemStatusId >= 50 && itemStatusId < 100)
      return 'workflow-bar-conceptreview';
    if (itemStatusId == 11) return 'workflow-bar-notpursued';
  }
  if (itemStatusId == 9) return 'workflow-bar-retracted';
  if (catId == 4 && item.props?.gcomStatus == 'Archived') {
    return 'workflow-bar-archived';
  }
  return 'workflow-bar-cat' + catId;
};
